import { useMemo, useState } from 'react'
import { Alert, Badge, Button, Checkbox, Group, Modal, Stack, Text } from '@mantine/core'
import { suggestSeries, type Entry } from '@racedash/shared'
import { saveEntries } from '../events/api'
import type { AttachedEvent, SessionSummary } from './useDashboardSource'

/**
 * Saying which of the stream's series names are yours.
 *
 * The session filter runs on the series name, and the series name is the least
 * reliable string in the feed: one channel was observed carrying two different
 * championships in an afternoon, and vendors spell the same championship three
 * ways. So when the guess fails, a person settles it — once per event, from
 * evidence rather than from memory.
 *
 * The evidence is which sessions the team's own numbers turned up in. That is
 * exactly the signal that cannot be trusted to decide on its own — numbers
 * repeat across series at one meeting, so a Ligier 436 and a Fun Cup 436 are
 * indistinguishable to a machine — and exactly the signal a person can read at
 * a glance, because they know which one they entered.
 *
 * Confirming narrows: a confirmed entry matches those names and nothing else,
 * so the fuzzy rule stops applying to it. Otherwise confirming "Fun Cup
 * Belgium" would keep dragging in "Fun Cup France".
 */
export default function SeriesResolver({
  event,
  sessions,
  orgId,
  onClose,
}: {
  event: AttachedEvent
  sessions: SessionSummary[]
  orgId: string
  onClose: () => void
}) {
  const [chosen, setChosen] = useState<Record<string, string[]>>(() =>
    Object.fromEntries(event.entries.map((e) => [e.carId, e.seriesMatched ?? []])),
  )
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  /** Every series string actually captured here, so nothing is unreachable. */
  const allSeries = useMemo(() => {
    const names = new Set<string>()
    for (const s of sessions) if (s.seriesNameRaw) names.add(s.seriesNameRaw)
    return [...names].sort()
  }, [sessions])

  const toggle = (carId: string, name: string) =>
    setChosen((prev) => {
      const current = prev[carId] ?? []
      return {
        ...prev,
        [carId]: current.includes(name)
          ? current.filter((n) => n !== name)
          : [...current, name],
      }
    })

  const save = async () => {
    setSaving(true)
    setError(null)
    try {
      const next: Entry[] = event.entries.map((e) => {
        const picked = chosen[e.carId] ?? []
        // An empty selection clears the confirmation rather than storing an
        // empty list, so the entry falls back to matching by name instead of
        // matching nothing at all — which would hide every session.
        const { seriesMatched: _dropped, ...rest } = e
        return picked.length > 0 ? { ...rest, seriesMatched: picked } : rest
      })
      await saveEntries(event.eventId, orgId, next)
      onClose()
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal opened onClose={onClose} title="Which sessions are yours?" size="lg">
      <Stack gap="lg">
        <Text size="sm" c="dimmed">
          {event.name} produced {allSeries.length} different series name
          {allSeries.length === 1 ? '' : 's'}. Tick the ones each of your cars is racing in.
        </Text>

        {event.entries.length === 0 && (
          <Alert color="yellow" variant="light">
            This event has no entries recorded, so there is nothing to match. Add the cars you are
            running first.
          </Alert>
        )}

        {event.entries.map((entry) => {
          const hits = suggestSeries(entry.number, sessions)
          const seen = new Set(hits.map((h) => h.seriesNameRaw))
          return (
            <Stack key={entry.carId} gap="xs">
              <Group gap="xs" align="baseline">
                <Text fw={600}>Car {entry.number}</Text>
                {entry.series && (
                  <Text size="sm" c="dimmed">
                    entered as “{entry.series}”
                  </Text>
                )}
              </Group>

              {hits.length === 0 && (
                <Text size="sm" c="dimmed">
                  This number has not appeared in any captured session yet, so there is nothing to
                  go on. Pick by name below, or wait until the car has been out.
                </Text>
              )}

              <Stack gap={6}>
                {/* Suggestions first, in order of how much they were seen —
                    then everything else, so a car that has not run yet can
                    still be pointed at the right series by hand. */}
                {[...hits.map((h) => h.seriesNameRaw), ...allSeries.filter((n) => !seen.has(n))].map(
                  (name) => {
                    const hit = hits.find((h) => h.seriesNameRaw === name)
                    return (
                      <Checkbox
                        key={name}
                        checked={(chosen[entry.carId] ?? []).includes(name)}
                        onChange={() => toggle(entry.carId, name)}
                        label={
                          <Group gap="xs" wrap="nowrap">
                            <span>{name}</span>
                            {hit && (
                              <Badge
                                size="sm"
                                variant="light"
                                color={hit.exclusive ? 'green' : 'yellow'}
                              >
                                {hit.exclusive
                                  ? `${entry.number} appears here`
                                  : `${entry.number} appears here too`}
                              </Badge>
                            )}
                          </Group>
                        }
                      />
                    )
                  },
                )}
              </Stack>

              {hits.length > 1 && (
                <Text size="xs" c="dimmed">
                  {entry.number} appears in more than one series here. Numbers repeat across
                  championships at one meeting, so only you can say which is yours.
                </Text>
              )}
            </Stack>
          )
        })}

        {error && (
          <Alert color="red" variant="light">
            {error}
          </Alert>
        )}

        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            Cancel
          </Button>
          <Button loading={saving} onClick={() => void save()} disabled={event.entries.length === 0}>
            Save
          </Button>
        </Group>
      </Stack>
    </Modal>
  )
}
